import { Component, OnInit } from '@angular/core';
import { AccountService } from '../../core/auth/account.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-monthly-unique-visitor',
  standalone: false,
  templateUrl: './monthly-unique-visitor.component.html',
  styleUrl: './monthly-unique-visitor.component.scss',
})
export class MonthlyUniqueVisitorComponent implements OnInit {
  data: any;
  options: any;
  private chartColors = {
    accent: '#db2a22',
    accentAlt: '#ff577e',
    textPrimary: '#f6f6f6',
    textMuted: '#b7bcc4',
    grid: '#3d404a',
  };

  constructor(
    private accountService: AccountService,
    private router: Router
  ) {
    this.applyTheme();
  }

  ngOnInit(): void {
    this.accountService.identity().subscribe((res) => {
      const account = res;
      if (account === null) this.router.navigate(['/account/login']);
    });

    this.accountService.getMonthlyUniqueVisitor().subscribe({
      next: (data) => {
        // Check if data and data.data exist
        if (!data || !data.data || !Array.isArray(data.data)) {
          console.warn('Invalid data received for monthly unique visitor chart:', data);
          this.data = { labels: [], datasets: [] };
          return;
        }

        const lLabels: number[] = [];
        let lData: number[] = [];
        const lLocation: string[] = [];
        const lDatasets: any[] = [];

        data.data.forEach((obj) => {
          if (obj && obj.location && obj.month !== undefined) {
            lLocation.push(obj.location);
            lLabels.push(obj.month);
          }
        });

        const uniqueLocation = [...new Set(lLocation.map((item) => item))];
        const uniqueLabels = [...new Set(lLabels.map((item) => item))];

        const palette = this.buildPalette(uniqueLocation.length);

        uniqueLocation.forEach((ul, index) => {
          lData = [];
          data.data.forEach((obj) => {
            if (ul === obj.location) {
              for (let i = 0; i < uniqueLabels.length; i++) {
                if (uniqueLabels[i] === obj.month) {
                  lData[i] = obj.visits || 0;
                  break;
                }
              }
            }
          });
          const ds = {
            label: ul,
            data: lData,
            backgroundColor: palette[index],
            borderRadius: 8,
            borderSkipped: false,
            maxBarThickness: 36,
          };
          lDatasets.push(ds);
        });

        this.data = {
          labels: uniqueLabels,
          datasets: lDatasets,
        };
      },
      error: (error) => {
        console.error('Error loading monthly unique visitor data:', error);
        this.data = { labels: [], datasets: [] };
      },
    });
  }

  private applyTheme(): void {
    const accent = this.getCssVar('--color-primary', this.chartColors.accent);
    const accentAlt = this.getCssVar('--brand-accent', this.chartColors.accentAlt);
    const textPrimary = this.getCssVar('--text-primary', this.chartColors.textPrimary);
    const textMuted = this.getCssVar('--text-secondary', this.chartColors.textMuted);
    const gridBase = this.getCssVar('--table-border', this.chartColors.grid);
    const grid = this.hexToRgba(gridBase, 0.45);

    this.chartColors = {
      accent,
      accentAlt,
      textPrimary,
      textMuted,
      grid,
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            color: this.chartColors.textMuted,
            usePointStyle: true,
            boxWidth: 8,
          },
        },
        title: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: this.chartColors.textMuted,
            beginAtZero: true,
            maxTicksLimit: 12,
            padding: 12,
          },
          grid: {
            display: true,
            color: this.chartColors.grid,
            borderColor: this.chartColors.grid,
          },
        },
        y: {
          ticks: {
            color: this.chartColors.textMuted,
            beginAtZero: true,
            maxTicksLimit: 10,
            padding: 12,
          },
          grid: {
            display: true,
            color: this.chartColors.grid,
            borderColor: this.chartColors.grid,
          },
        },
      },
    };
  }

  private buildPalette(count: number): string[] {
    const base = [
      this.chartColors.accent,
      this.chartColors.accentAlt,
      '#6bd0f5',
      '#f9c74f',
      '#43aa8b',
      '#f8961e',
      '#4d96ff',
    ];
    const palette: string[] = [];
    for (let i = 0; i < count; i++) {
      palette.push(base[i % base.length]);
    }
    return palette;
  }

  private getCssVar(name: string, fallback: string): string {
    if (typeof document === 'undefined') {
      return fallback;
    }
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }

  private hexToRgba(value: string, alpha: number): string {
    const hex = value.replace('#', '').trim();
    if (hex.length !== 6) {
      return value;
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
